import {
  IHttpPerfSample,
  INavigationPerfSample,
  IPerfSuggestion,
  PerfSuggestionCode,
} from './models/perf-suggestion.model';
import {
  CHATTY_NAVIGATION_REQUESTS,
  DUPLICATE_WINDOW_MS,
  HTTP_ERROR_STATUS_FROM,
  LARGE_PAYLOAD_BYTES,
  NAVIGATION_HTTP_BOUND_RATIO,
  SMALL_PAYLOAD_BYTES,
  WATERFALL_GAP_MS,
  WATERFALL_LOOKBACK_MS,
  WATERFALL_MIN_CHAIN,
} from './perf-thresholds.const';

const GET_METHOD = 'GET';

function formatBytes(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`;
}

/**
 * Length of the longest run of requests where each one started right after the
 * previous one finished - i.e. calls that could probably have run in parallel.
 * Returns the best chain length ending at each sample (same order as input).
 */
export function chainLengthsEndingAt(samples: IHttpPerfSample[]): number[] {
  const sorted = samples
    .map((sample, index) => ({ sample, index }))
    .sort((a, b) => a.sample.startMs - b.sample.startMs);
  const lengths = new Array<number>(samples.length).fill(1);

  sorted.forEach((current, i) => {
    for (let j = 0; j < i; j++) {
      const previous = sorted[j].sample;
      const gap = current.sample.startMs - previous.endMs;
      if (gap >= 0 && gap <= WATERFALL_GAP_MS) {
        lengths[current.index] = Math.max(
          lengths[current.index],
          lengths[sorted[j].index] + 1
        );
      }
    }
  });

  return lengths;
}

function isDuplicate(sample: IHttpPerfSample, history: IHttpPerfSample[]) {
  return (
    sample.method === GET_METHOD &&
    history.some(
      (other) =>
        other !== sample &&
        other.method === GET_METHOD &&
        other.url === sample.url &&
        Math.abs(other.startMs - sample.startMs) <= DUPLICATE_WINDOW_MS
    )
  );
}

/**
 * Suggestions for a slow HTTP request. `history` is the recent samples
 * (including `sample` itself). Callers are expected to only call this for
 * requests already over the slow threshold.
 */
export function adviseSlowHttp(
  sample: IHttpPerfSample,
  history: IHttpPerfSample[]
): IPerfSuggestion[] {
  const suggestions: IPerfSuggestion[] = [];
  const failed = sample.status === 0 || sample.status >= HTTP_ERROR_STATUS_FROM;

  if (failed) {
    suggestions.push({
      code: PerfSuggestionCode.SLOW_FAILURE,
      message: `Slow failure (status ${sample.status}): check timeout and retry settings, and avoid retrying non-idempotent calls.`,
    });
  }

  if (isDuplicate(sample, history)) {
    suggestions.push({
      code: PerfSuggestionCode.DUPLICATE_REQUEST,
      message:
        'Same GET was requested more than once close together: cache it in the service (shareReplay / keyed cache) or load it in a parent and share it.',
    });
  }

  const nearby = history.filter(
    (other) =>
      other.endMs <= sample.endMs &&
      other.startMs >= sample.startMs - WATERFALL_LOOKBACK_MS
  );
  const chain = chainLengthsEndingAt(nearby)[nearby.indexOf(sample)];
  if (chain >= WATERFALL_MIN_CHAIN) {
    suggestions.push({
      code: PerfSuggestionCode.REQUEST_WATERFALL,
      message: `Likely request waterfall (${chain} calls back to back): run independent calls in parallel (forkJoin / combineLatest) or load them in a resolver.`,
    });
  }

  if (!failed) {
    if (
      sample.sizeBytes !== undefined &&
      sample.sizeBytes > LARGE_PAYLOAD_BYTES
    ) {
      suggestions.push({
        code: PerfSuggestionCode.LARGE_PAYLOAD,
        message: `Large payload (${formatBytes(sample.sizeBytes)}): add pagination or field selection, or lazy-load this data.`,
      });
    } else if (
      sample.sizeBytes === undefined ||
      sample.sizeBytes < SMALL_PAYLOAD_BYTES
    ) {
      suggestions.push({
        code: PerfSuggestionCode.SLOW_SERVER_RESPONSE,
        message:
          'Slow for a small response: likely server-side latency. Check the endpoint (queries, indexes) with the backend team.',
      });
    }
  }

  return suggestions;
}

/** Total time in `[start, end]` covered by at least one of the samples. */
function coveredMs(samples: IHttpPerfSample[], start: number, end: number) {
  const intervals = samples
    .map((s) => [Math.max(s.startMs, start), Math.min(s.endMs, end)])
    .sort((a, b) => a[0] - b[0]);
  let covered = 0;
  let cursor = start;

  for (const [from, to] of intervals) {
    const effectiveFrom = Math.max(from, cursor);
    if (to > effectiveFrom) {
      covered += to - effectiveFrom;
      cursor = to;
    }
  }

  return covered;
}

/** Suggestions for a slow navigation, given the recent HTTP samples. */
export function adviseSlowNavigation(
  navigation: INavigationPerfSample,
  history: IHttpPerfSample[]
): IPerfSuggestion[] {
  const duration = navigation.endMs - navigation.startMs;
  const overlapping = history.filter(
    (s) => s.endMs > navigation.startMs && s.startMs < navigation.endMs
  );

  if (overlapping.length === 0) {
    return [
      {
        code: PerfSuggestionCode.NAVIGATION_WITHOUT_NETWORK,
        message:
          'Slow with no HTTP calls: likely a large lazy-loaded chunk or heavy component init. Check the route chunk size and defer heavy work (@defer).',
      },
    ];
  }

  const suggestions: IPerfSuggestion[] = [];
  const httpShare =
    coveredMs(overlapping, navigation.startMs, navigation.endMs) / duration;

  if (httpShare >= NAVIGATION_HTTP_BOUND_RATIO) {
    suggestions.push({
      code: PerfSuggestionCode.NAVIGATION_BLOCKED_BY_HTTP,
      message: `Navigation spent ${Math.round(httpShare * 100)}% of its time waiting on HTTP: check guards and resolvers, and render the page shell before loading data.`,
    });
  }

  if (Math.max(...chainLengthsEndingAt(overlapping)) >= WATERFALL_MIN_CHAIN) {
    suggestions.push({
      code: PerfSuggestionCode.REQUEST_WATERFALL,
      message:
        'Likely request waterfall during this navigation: run independent calls in parallel (forkJoin / combineLatest) or load them in a resolver.',
    });
  }

  if (overlapping.length > CHATTY_NAVIGATION_REQUESTS) {
    suggestions.push({
      code: PerfSuggestionCode.CHATTY_NAVIGATION,
      message: `${overlapping.length} HTTP calls during one navigation: batch endpoints or defer non-essential calls.`,
    });
  }

  return suggestions;
}
